// KpiCard — MD3 Card для отображения одного KPI (label + value + icon).
// Используется на DashboardPage для tasks count, deals count, events count, contacts count.
import type { ReactNode } from 'react';
import { Card, CardContent, Stack, Typography, Box } from '@mui/material';

interface KpiCardProps {
  /** Подпись KPI. */
  label: string;
  /** Числовое значение. */
  value: number | string;
  /** Иконка слева от значения. */
  icon?: ReactNode;
  /** Цвет иконки (по умолчанию primary). */
  iconColor?: 'primary' | 'secondary' | 'tertiary' | 'error' | 'success' | 'warning';
  /** Опциональный sub-label (например, "за неделю" или "+3 за день"). */
  subLabel?: string;
}

/** MD3 Card для одного KPI. */
export function KpiCard({ label, value, icon, iconColor = 'primary', subLabel }: KpiCardProps): JSX.Element {
  return (
    <Card data-testid="kpi-card" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          {icon ? (
            <Box
              sx={{
                bgcolor: `${iconColor}.main`,
                color: `${iconColor}.contrastText`,
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.85,
                '& > *': { color: 'inherit' },
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {value}
            </Typography>
            {subLabel ? (
              <Typography variant="caption" color="text.secondary">
                {subLabel}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
